import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import Lottie from 'react-lottie-player';
import { appClient } from 'src/config/apollo';
import { useGetScheduledContentActivityLazyQuery } from 'src/modules/scheduledContent/documents/getScheduledContentActivity.generated';
import successAnimation from 'src/public/animations/colorful_confetti.json';
import { color, font } from 'src/shared/styles/variables';
import { useHistory, Redirect, RouteComponentProps } from 'react-router-dom';
import { BodyTextMedium, Button, HeadingText50 } from '@modernhealth/vital-components';
import { DailyPausePageWrapper } from '../components/DailyPausePageWrapper';
import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';
import { useFlags } from 'launchdarkly-react-client-sdk';
import moment from 'moment';
import { DailyPauseCelebrationTestId } from 'src/modules/scheduledContent/pages/testIds';

export type DailyPauseCelebrationProps = { celebrationTitleKeyIndex: number } & RouteComponentProps<
    {},
    {},
    { uuid?: string; dailyPauseRemindersPageVisited?: boolean } | undefined
>;

export const DailyPauseCelebration: React.FC<DailyPauseCelebrationProps> = ({
    location,
    celebrationTitleKeyIndex,
}) => {
    const { uuid, dailyPauseRemindersPageVisited } = location.state || {};

    const { t } = useTranslation();
    const history = useHistory();
    const {
        releaseDiginShowDailyPauseCalendarRemindersWeb,
        releaseDiginShowDailyPauseNotificationsWeb,
    } = useFlags();
    const dailyPauseSettingsEnabled =
        releaseDiginShowDailyPauseCalendarRemindersWeb ||
        releaseDiginShowDailyPauseNotificationsWeb;
    const currentYearMonth = moment().format('Y-MM');

    const [getScheduledContentActivity, { data, loading, error }] =
        useGetScheduledContentActivityLazyQuery({
            client: appClient,
            variables: { from: `${currentYearMonth}-01` },
        });

    useEffect(() => {
        getScheduledContentActivity();
    }, [uuid]);

    const handleDailyPauseDoneRedirect = () => {
        if (dailyPauseRemindersPageVisited || !dailyPauseSettingsEnabled) {
            history.push('/home');
        }

        if (!dailyPauseRemindersPageVisited && dailyPauseSettingsEnabled) {
            history.push('/pause/additional-options', {
                dailyPauseRemindersPageVisited,
            });
        }
    };

    if (!uuid || error) {
        return <Redirect to="/dashboard" />;
    }

    return (
        <DailyPausePageWrapper
            onRedirect={handleDailyPauseDoneRedirect}
            testId={DailyPauseCelebrationTestId.Container}
        >
            {loading || !data?.scheduledContentActivity ? (
                <CenteredSpinner />
            ) : (
                <>
                    <ConfettiContainer>
                        <SuccessAnimation animationData={successAnimation} loop play />
                        <HeadingText50 centered marginBottom={20}>
                            {t([
                                `dailyPause:celebration_modal.title.${celebrationTitleKeyIndex}`,
                                `dailyPause:celebration_modal.title`,
                            ])}
                        </HeadingText50>
                        <CompletedCount>
                            {data.scheduledContentActivity.completedCount}
                        </CompletedCount>
                    </ConfettiContainer>
                    <BodyTextMedium centered marginTop={32} marginBottom={90}>
                        {t('dailyPause:celebration_modal.congrats_text', {
                            count: data.scheduledContentActivity.completedCount || 1,
                        })}
                    </BodyTextMedium>

                    <ButtonContainer>
                        {dailyPauseRemindersPageVisited && dailyPauseSettingsEnabled && (
                            <StyledButton
                                variant="primary"
                                size="large"
                                onClick={() =>
                                    history.push('/pause/additional-options', {
                                        dailyPauseRemindersPageVisited,
                                    })
                                }
                                title={t('dailyPause:celebration_modal.set_daily_pause_reminder')}
                            >
                                {t('dailyPause:celebration_modal.set_daily_pause_reminder')}
                            </StyledButton>
                        )}
                        <StyledButton
                            variant="secondary"
                            size="large"
                            onClick={handleDailyPauseDoneRedirect}
                        >
                            {t('dailyPause:celebration_modal.cta_done')}
                        </StyledButton>
                    </ButtonContainer>
                </>
            )}
        </DailyPausePageWrapper>
    );
};

const ConfettiContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: 220,
});

const SuccessAnimation = styled(Lottie)({
    width: 400,
    maxHeight: 220,
    opacity: 0.7,
    position: 'absolute',
    overflow: 'hidden',
    left: 'calc(50% - 200px)',
});

const CompletedCount = styled.p({
    ...font.heavy(font.size.biggest),
    color: color.ebonyClay,
    textAlign: 'center',
    lineHeight: '80px',
});

const ButtonContainer = styled.div({
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
});

const StyledButton = styled(Button)({
    maxWidth: '250px',
    width: '100%',
});
